/**
 * LayerDrawer — slim, collapsible layer list pinned to the right edge of the
 * CAD canvas (AutoCAD-style lightbulbs). A chevron tab shows/hides it; each
 * row toggles the layer on/off (shared team visibility, re-renders the sheet).
 */
import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Lightbulb, LightbulbOff } from "lucide-react";
import { toast } from "sonner";
import { cadUpdate } from "./api";

export default function LayerDrawer({ drawing, cad, onCadChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const pending = useRef(null);
  const layers = cad?.layers || [];
  if (!cad || !layers.length) return null;
  const hidden = new Set(cad.hidden || []);
  const shown = layers.filter((l) => !q || l.name.toLowerCase().includes(q.toLowerCase()));

  const save = (next) => {
    onCadChange?.({ ...cad, hidden: [...next].sort(), status: "rendering" });
    clearTimeout(pending.current);
    pending.current = setTimeout(async () => {
      try { onCadChange?.(await cadUpdate(drawing.drawing_id, { hidden: [...next] })); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not save layer visibility"); }
    }, 500);
  };
  const toggle = (name) => { const n = new Set(hidden); if (n.has(name)) n.delete(name); else n.add(name); save(n); };

  return (
    <div className={`pointer-events-none absolute inset-y-0 right-0 z-20 flex items-stretch transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-[calc(100%-28px)]"}`} data-testid="layer-drawer" data-open={open}>
      <button type="button" onClick={() => setOpen((o) => !o)} title={open ? "Hide layer list" : "Show layer list"} aria-label={open ? "Hide layer list" : "Show layer list"}
        className="pointer-events-auto my-auto flex h-16 w-7 items-center justify-center rounded-l-lg border border-r-0 border-white/15 bg-[#14171c] text-[#f97316] shadow-lg hover:bg-[#1c2026]"
        data-testid="layer-drawer-toggle">
        {open ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
      <div className="pointer-events-auto flex w-64 flex-col border-l border-white/10 bg-[#14171c]/95 text-white shadow-2xl backdrop-blur">
        <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#f97316]">Layers · {layers.length}</span>
          <span className="font-mono text-[10px] text-white/40" data-testid="layer-drawer-hidden-count">{hidden.size} off</span>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find layer…" className="mx-3 my-2 rounded-md border border-white/15 bg-[#0b0d10] px-2 py-1 text-xs text-white placeholder:text-white/30 focus:outline-none" data-testid="layer-drawer-search" />
        <div className="flex gap-2 px-3 pb-2 font-mono text-[9px] uppercase tracking-widest text-white/50">
          <button type="button" onClick={() => save(new Set())} className="hover:text-white" data-testid="layer-drawer-all-on">All on</button>
          <button type="button" onClick={() => save(new Set(layers.map((l) => l.name)))} className="hover:text-white" data-testid="layer-drawer-all-off">All off</button>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
          {shown.map((l) => {
            const off = hidden.has(l.name);
            return (
              <li key={l.name}>
                <button type="button" onClick={() => toggle(l.name)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs hover:bg-white/5 ${off ? "text-white/35" : "text-white/85"}`} data-testid={`layer-bulb-${l.name}`} aria-pressed={!off}>
                  {off ? <LightbulbOff className="h-3.5 w-3.5 shrink-0 text-white/30" /> : <Lightbulb className="h-3.5 w-3.5 shrink-0 text-yellow-400" />}
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-black/30" style={{ background: l.color || "#999" }} />
                  <span className="truncate">{l.name}</span>
                </button>
              </li>
            );
          })}
          {!shown.length && <li className="px-2 py-3 text-center text-xs text-white/40">No layers match</li>}
        </ul>
      </div>
    </div>
  );
}
